import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { ChevronLeft, ChevronRight, Users, UserCog, Shield, Plus, ArrowRight } from 'lucide-react';
import UnifiedTeamManagementModal from './UnifiedTeamManagementModal';
import { useNavigate } from 'react-router-dom';

interface TeamMember {
  user_id: string;
  position?: string;
  role?: string;
  joined_at?: string;
  added_at?: string;
  user_details?: {
    username?: string;
    first_name?: string;
    last_name?: string;
    profile_photo_url?: string;
    slug?: string;
  };
}

interface MobileTeamManagementProps {
  teamId: number;
  teamName: string;
  teamSport?: string;
  currentUserId?: string;
  canManage: boolean;
}

export const MobileTeamManagement = ({ teamId, teamName, teamSport, currentUserId, canManage }: MobileTeamManagementProps) => {
  const navigate = useNavigate();
  const [players, setPlayers] = useState<TeamMember[]>([]);
  const [coaches, setCoaches] = useState<TeamMember[]>([]);
  const [supportStaff, setSupportStaff] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [showManagementModal, setShowManagementModal] = useState(false);
  
  // Separate current index state for each section
  const [playersCurrentIndex, setPlayersCurrentIndex] = useState(0);
  const [coachesCurrentIndex, setCoachesCurrentIndex] = useState(0);
  const [supportCurrentIndex, setSupportCurrentIndex] = useState(0);

  useEffect(() => {
    loadTeamMembers();
  }, [teamId]);

  const loadTeamMembers = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadPlayers(),
        loadCoaches(),
        loadSupportStaff()
      ]);
    } catch (error) {
      console.error('Error loading team members:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPlayers = async () => {
    try {
      const { data: teamPlayers, error } = await supabase
        .from('team_players')
        .select('*')
        .eq('team_id', teamId);

      if (error) throw error;

      const playersWithDetails = await Promise.all(
        (teamPlayers || []).map(async (player) => {
          const userDetails = await getUserDetails(player.user_id);
          return {
            ...player,
            user_details: userDetails
          };
        })
      );

      setPlayers(playersWithDetails);
    } catch (error) {
      console.error('Error loading players:', error);
    }
  };

  const loadCoaches = async () => {
    try {
      const { data: teamCoaches, error } = await supabase
        .from('team_coaches')
        .select('*')
        .eq('team_id', teamId);

      if (error) throw error;

      const coachesWithDetails = await Promise.all(
        (teamCoaches || []).map(async (coach) => {
          const userDetails = await getUserDetails(coach.coach_id);
          return {
            user_id: coach.coach_id,
            role: coach.role,
            added_at: coach.added_at,
            user_details: userDetails
          };
        })
      );

      setCoaches(coachesWithDetails);
    } catch (error) {
      console.error('Error loading coaches:', error);
    }
  };

  const loadSupportStaff = async () => {
    try {
      const { data: supportConnections, error } = await supabase
        .from('team_connections')
        .select('*')
        .eq('team_id', teamId)
        .eq('connection_type', 'support');

      if (error) throw error;

      const supportWithDetails = await Promise.all(
        (supportConnections || []).map(async (support) => {
          const userDetails = await getUserDetails(support.user_id);
          return {
            user_id: support.user_id,
            role: support.role || 'Support Staff',
            added_at: support.created_at,
            user_details: userDetails
          };
        })
      );

      setSupportStaff(supportWithDetails);
    } catch (error) {
      console.error('Error loading support staff:', error);
    }
  };

  const getUserDetails = async (userId: string) => {
    try {
      // Try internal users first for better under-13 user support
      const { data: internalUser } = await supabase
        .from('internal_users')
        .select('username, role, age_group')
        .eq('id', userId)
        .maybeSingle();

      if (internalUser) {
        // Get profile data including age information
        const { data: profile } = await supabase
          .from('internal_user_profiles')
          .select('age, profile_photo_url')
          .eq('user_id', userId)
          .maybeSingle();

        return {
          username: internalUser.username,
          first_name: '',
          last_name: '',
          profile_photo_url: profile?.profile_photo_url || null,
          age: profile?.age || null,
          age_group: internalUser.age_group
        };
      }

      // Fallback to Supabase profiles
      const { data: profile } = await supabase
        .from('profiles')
        .select('username, first_name, last_name, profile_photo_url, slug')
        .eq('id', userId)
        .single();

      if (profile) return profile;

      return {};
    } catch (error) {
      console.error('Error fetching user details:', error);
      return {};
    }
  };

  const navigateToProfile = (member: TeamMember) => {
    // Use slug or username for profile navigation
    const profileIdentifier = member.user_details?.slug || 
                              member.user_details?.username;
    
    if (profileIdentifier) {
      navigate(`/${profileIdentifier}`);
    } else {
      // Fallback for users without slug/username
      console.warn('No profile identifier found for user:', member.user_details);
    }
  };

  const renderMemberCard = (member: TeamMember, type: 'player' | 'coach' | 'support') => {
    const displayName = member.user_details?.username || 
                       `${member.user_details?.first_name || ''} ${member.user_details?.last_name || ''}`.trim() ||
                       'USER NAME';

    const position = member.position || member.role || 'Position';

    return (
      <div key={member.user_id} className="text-center flex-1 min-w-0 max-w-[30%] relative">
        {/* Large circular profile photo */}
        <div className="relative mx-auto mb-4">
          <div className="w-20 h-20 mx-auto bg-gray-600 rounded-full flex items-center justify-center overflow-hidden border-2 border-gray-500">
            {member.user_details?.profile_photo_url ? (
              <img
                src={member.user_details.profile_photo_url}
                alt={displayName}
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              <div className="text-white text-xs text-center leading-tight">
                User Profile<br />Photo
              </div>
            )}
          </div>
          {/* Green arrow button positioned on top right edge */}
          <button 
            onClick={() => navigateToProfile(member)}
            className="absolute -top-1 -right-1 w-6 h-6 bg-sports-green hover:bg-sports-green/80 rounded-sm flex items-center justify-center transition-colors"
          >
            <ArrowRight className="h-3 w-3 text-black" />
          </button>
        </div>
        
        {/* User name */}
        <div className="text-white text-sm font-bold uppercase tracking-wide mb-1">
          {displayName}
        </div>
        
        {/* Position */}
        <div className="text-gray-400 text-xs">
          {position}
        </div>
      </div>
    );
  };

  const renderSection = (title: string, members: TeamMember[], type: 'player' | 'coach' | 'support', icon: React.ReactNode) => {
    const membersPerPage = 3;
    const totalPages = Math.ceil(members.length / membersPerPage);
    
    // Get the appropriate current index and setter based on section type
    let currentIndex = 0;
    let setCurrentIndex: (value: number | ((prev: number) => number)) => void = () => {};
    
    switch (type) {
      case 'player':
        currentIndex = playersCurrentIndex;
        setCurrentIndex = setPlayersCurrentIndex;
        break;
      case 'coach':
        currentIndex = coachesCurrentIndex;
        setCurrentIndex = setCoachesCurrentIndex;
        break;
      case 'support':
        currentIndex = supportCurrentIndex;
        setCurrentIndex = setSupportCurrentIndex;
        break;
    }
    
    const handleSwipeLeft = () => {
      setCurrentIndex((prev) => prev < totalPages - 1 ? prev + 1 : 0);
    };

    const handleSwipeRight = () => {
      setCurrentIndex((prev) => prev > 0 ? prev - 1 : totalPages - 1);
    };

    const currentPageMembers = members.slice(currentIndex * membersPerPage, (currentIndex + 1) * membersPerPage);

    return (
      <Card className="bg-gray-800 border-gray-600 mb-6">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {icon}
              <span className="text-white font-semibold">{title}</span>
               {canManage && (
                 <Button
                   size="sm"
                   className="bg-sports-green text-black hover:bg-sports-green/80 font-bold px-3 py-1 text-xs rounded-md"
                   onClick={() => setShowManagementModal(true)}
                 >
                   + MANAGE
                 </Button>
               )}
            </div>
            {/* Pagination dots - always show 5 dots */}
            <div className="flex gap-2">
              {Array.from({ length: 5 }, (_, i) => (
                <button
                  key={i}
                  onClick={() => i < totalPages ? setCurrentIndex(i) : null}
                  className={`w-3 h-3 rounded-full transition-colors ${
                    i === currentIndex ? 'bg-sports-green' : 'bg-gray-500'
                  }`}
                />
              ))}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {members.length > 0 ? (
            <div className="relative px-4">
              <div className="flex gap-8 justify-center">
                {currentPageMembers.map((member) => renderMemberCard(member, type))}
                {/* Fill remaining slots to maintain spacing */}
                {Array.from({ 
                  length: Math.max(0, membersPerPage - currentPageMembers.length) 
                }, (_, i) => (
                  <div key={`empty-${i}`} className="flex-1 min-w-0 max-w-[30%] invisible">
                    <div className="w-20 h-20 mx-auto mb-4"></div>
                    <div className="text-sm mb-1">Hidden</div>
                  </div>
                ))}
              </div>
              
              {totalPages > 1 && (
                <>
                  <button
                    onClick={handleSwipeRight}
                    className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-2 bg-gray-600 hover:bg-gray-500 text-white p-2 rounded-full shadow-lg z-10 border border-gray-500"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    onClick={handleSwipeLeft}
                    className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-2 bg-gray-600 hover:bg-gray-500 text-white p-2 rounded-full shadow-lg z-10 border border-gray-500"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-400">No {title.toLowerCase()} added yet.</div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="bg-gray-800 border-gray-700">
            <CardHeader>
              <div className="h-6 bg-gray-700 rounded animate-pulse"></div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="h-16 bg-gray-700 rounded animate-pulse"></div>
                <div className="h-16 bg-gray-700 rounded animate-pulse"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {/* Players Section */}
        {renderSection('Players', players, 'player', <Users className="h-5 w-5" />)}
        
        {/* Coaches Section */}
        {renderSection('Coaches', coaches, 'coach', <UserCog className="h-5 w-5" />)}
        
        {/* Support Staff Section */}
        {renderSection('Support Staff', supportStaff, 'support', <Shield className="h-5 w-5" />)}

      </div>

      {/* Unified Team Management Modal */}
      <UnifiedTeamManagementModal
        isOpen={showManagementModal}
        onClose={() => setShowManagementModal(false)}
        team={{
          team_id: teamId,
          team_name: teamName,
          sport_type: teamSport
        }}
        onUpdate={loadTeamMembers}
      />
    </>
  );
};